import React from 'react';
import { motion } from 'framer-motion';
import { personaDetails } from '@/data/quizData';
import SafeIcon from '@/common/SafeIcon';

export default function ResultsScreen({ results, firstName }) {
  const { scores, strongest, weakest } = results;

  const personasData = [
    { id: 'christian', label: 'The Christian', score: scores.christian },
    { id: 'creator', label: 'The Creator', score: scores.creator },
    { id: 'caretaker', label: 'The Caretaker', score: scores.caretaker },
    { id: 'connector', label: 'The Connector', score: scores.connector },
  ];

  const strongestDetails = personaDetails[strongest];
  const weakestDetails = personaDetails[weakest];

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="space-y-10"
    >
      <div className="text-center md:max-w-2xl mx-auto">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-sm mb-6 text-brand-orange">
          <SafeIcon name="Award" className="text-3xl" />
        </div>
        <h2 className="text-3xl md:text-5xl font-heading font-bold text-brand-blue mb-4 tracking-tight">
          Here's your GospelWise<br/>Stewardship Profile{firstName ? `, ${firstName}` : ''}
        </h2>
        <p className="text-lg text-gray-600 font-body">Review your detailed persona scores below.</p>
      </div>

      <div className="bg-white p-8 md:p-10 rounded-[2.5rem] shadow-xl shadow-brand-blue/5 border border-brand-blue/5">
        <h3 className="font-heading font-bold text-xl mb-8 text-brand-blue text-center uppercase tracking-widest text-sm">Your Persona Breakdown</h3>
        <div className="space-y-8">
          {personasData.map((p, index) => (
            <div key={p.id} className="relative">
              <div className="flex justify-between text-sm font-body mb-3 px-1">
                <span className="font-bold text-gray-800 text-base md:text-lg flex items-center">
                  {p.id === strongest && <SafeIcon name="Star" className="text-brand-orange mr-2" />}
                  {p.label}
                </span>
                <span className="text-brand-blue font-bold text-lg">{p.score}<span className="text-gray-400 text-sm">/12</span></span>
              </div>
              <div className="h-4 w-full bg-brand-beige/50 rounded-full overflow-hidden shadow-inner">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${(p.score / 12) * 100}%` }}
                  transition={{ duration: 1.2, ease: 'easeOut', delay: index * 0.15 }}
                  className={`h-full rounded-full relative overflow-hidden ${p.id === weakest ? 'bg-brand-orange' : 'bg-brand-blue'}`}
                >
                  <div className="absolute inset-0 bg-white/20 w-full h-full transform -skew-x-12 translate-x-full animate-[shimmer_2s_infinite]"></div>
                </motion.div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white p-8 md:p-10 rounded-[2.5rem] border border-brand-blue/10 shadow-lg shadow-brand-blue/5 flex flex-col relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-brand-blue/5 rounded-bl-full -z-0"></div>
          <div className="flex items-center space-x-3 mb-6 relative z-10">
            <div className="w-10 h-10 bg-brand-blue/10 rounded-full flex items-center justify-center text-brand-blue">
              <SafeIcon name="TrendingUp" className="text-xl" />
            </div>
            <h3 className="font-heading font-bold text-sm text-gray-500 uppercase tracking-wider">Strongest Area</h3>
          </div>
          <h4 className="font-heading font-bold text-2xl text-brand-blue mb-4 relative z-10">
            {strongestDetails.name}
          </h4>
          <p className="font-body text-gray-700 leading-relaxed text-lg flex-1 relative z-10">
            {strongestDetails.affirmation}
          </p>
        </div>

        <div className="bg-brand-orange/5 p-8 md:p-10 rounded-[2.5rem] border border-brand-orange/20 shadow-lg shadow-brand-orange/5 flex flex-col relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-brand-orange/10 rounded-bl-full -z-0"></div>
          <div className="flex items-center space-x-3 mb-6 relative z-10">
            <div className="w-10 h-10 bg-brand-orange/20 rounded-full flex items-center justify-center text-brand-orange">
              <SafeIcon name="Target" className="text-xl" />
            </div>
            <h3 className="font-heading font-bold text-sm text-brand-orange uppercase tracking-wider">Growth Opportunity</h3>
          </div>
          <h4 className="font-heading font-bold text-2xl text-brand-orange mb-4 relative z-10">
            {weakestDetails.name}
          </h4>
          <p className="font-body text-gray-700 leading-relaxed text-lg mb-8 flex-1 relative z-10">
            {weakestDetails.gap}
          </p>
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-brand-orange/10 relative z-10">
            <p className="text-xs font-heading font-bold text-gray-400 uppercase tracking-wider mb-2">Recommended Next Step</p>
            <div className="font-body font-bold text-brand-blue flex items-center">
              {weakestDetails.offerName}
            </div>
          </div>
        </div>
      </div>

      <div className="text-center pt-8 flex flex-col items-center">
        <a 
          href={weakestDetails.offerLink} 
          className="bg-brand-orange text-white font-heading font-bold py-5 px-12 rounded-full text-lg hover:bg-[#E56A42] shadow-xl shadow-brand-orange/30 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center w-full md:w-auto space-x-3"
        >
          <span>Explore {weakestDetails.offerName}</span>
          <SafeIcon name="ArrowRight" />
        </a>
      </div>
    </motion.div>
  );
}